function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef } from 'react';
import styled from 'styled-components';
import Downshift from 'downshift';
import InputWrapper from '../input-wrapper';
import Icon from '../../icon';
import { white, gray50, gray100, gray400, gray800, red600, focusShadow } from '../../theme/colors';
import { size4, size8, size24, size40, CONTROL_SIZE_MAP } from '../../theme/scale';
import { duration150, easeInOut } from '../../theme/animation';
import { fontBuenosAiresRegular, fontRegular } from '../../theme/fonts';
const Input = styled.input`
  ${({
  $size
}) => $size && {
  ...CONTROL_SIZE_MAP[$size]
}}
  border: 1px solid;
  border-color: ${({
  hasErrorMessage
}) => hasErrorMessage ? red600 : gray400};
  border-radius: ${size4};
  color: ${gray800};
  font-family: ${({
  joyableTheme
}) => joyableTheme ? fontBuenosAiresRegular : fontRegular};
  transition: border ${duration150} ${easeInOut},
    box-shadow ${duration150} ${easeInOut};
  width: 100%;
  padding-right: ${size40};

  ${({
  clearOnFocus
}) => !clearOnFocus && 'cursor: pointer;'}

  &:hover {
    background: ${gray50};
  }

  &:focus {
    background: ${white};
    border-color: ${({
  hasErrorMessage
}) => hasErrorMessage ? red600 : gray400};
    box-shadow: 0 0 0 3px ${focusShadow};
    outline: none;
  }

  &[disabled] {
    background: ${gray100};
    cursor: not-allowed;
  }
`;
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: block;
`;
const ActionWrapper = styled.div`
  position: relative;
`;
const DropdownTrigger = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 100%;
  bottom: ${size4};
  display: inline-flex;
  height: ${size24};
  justify-content: center;
  padding: 0;
  position: absolute;
  right: ${size8};
  top: 50%;
  transform: translateY(-50%);
  transition: background ${duration150} ${easeInOut},
    box-shadow ${duration150} ${easeInOut};
  width: ${size24};

  ${({
  clearOnFocus
}) => !clearOnFocus && 'pointer-events: none;'}

  &:hover {
    background: ${gray100};
    cursor: pointer;
  }

  &:focus {
    box-shadow: 0 0 0 3px ${focusShadow};
    outline: none;
  }
`;
export const DropdownWrapper = ({
  initialValue = '',
  label,
  renderChildren,
  onChange,
  itemToString,
  formattedInputValue,
  placeholder,
  hint,
  errorMessage,
  size = 'md',
  clearOnFocus,
  disabled = false,
  allowReset = false,
  hideLabel = false,
  smallLabel = false,
  caption,
  closedIcon = 'ChevronUp',
  openedIcon = 'ChevronDown',
  iconColor = 'gray900',
  joyableTheme = false,
  ...props
}) => {
  const onInputFocus = setState => {
    if (clearOnFocus) setState({
      inputValue: ''
    });
  };
  const onTrigger = (isOpen, openMenu, closeMenu) => {
    isOpen ? closeMenu() : openMenu();
  };
  const inputEl = useRef();
  const preventUpdate = (previousValue, currentValue) => !clearOnFocus && previousValue !== '' && currentValue === '' && !allowReset;
  const stateReducer = (state, changes) => {
    if (preventUpdate(state.inputValue, changes.inputValue)) {
      return {
        ...state,
        ...changes,
        inputValue: state.inputValue
      };
    }
    return {
      ...state,
      ...changes
    };
  };
  return /*#__PURE__*/React.createElement(Downshift, {
    selectedItem: initialValue,
    onChange: val => {
      !!onChange && onChange(val);
      inputEl?.current?.blur();
    },
    itemToString: itemToString,
    stateReducer: stateReducer
  }, ({
    setState,
    getInputProps,
    getRootProps,
    getItemProps,
    getMenuProps,
    getLabelProps,
    getToggleButtonProps,
    openMenu,
    closeMenu,
    inputValue,
    highlightedIndex,
    selectedItem,
    isOpen
  }) => /*#__PURE__*/React.createElement(Wrapper, getRootProps(), /*#__PURE__*/React.createElement(InputWrapper, {
    label,
    hint,
    errorMessage,
    hideLabel,
    caption,
    smallLabel,
    joyableTheme,
    labelProps: getLabelProps()
  }, /*#__PURE__*/React.createElement(ActionWrapper, null, /*#__PURE__*/React.createElement(Input, _extends({}, getInputProps(), {
    value: formattedInputValue || inputValue || '',
    $size: size,
    ref: inputEl,
    hasErrorMessage: !!errorMessage,
    onFocus: () => onInputFocus(setState),
    onClick: () => onTrigger(isOpen, openMenu, closeMenu),
    "data-testid": "dropdownWrapperInput",
    clearOnFocus: clearOnFocus,
    joyableTheme: joyableTheme,
    placeholder,
    disabled
  }, props)), /*#__PURE__*/React.createElement(DropdownTrigger, _extends({
    clearOnFocus,
    tabIndex: "-1",
    onFocus: () => onInputFocus(setState),
    onClick: () => onTrigger(isOpen, openMenu, closeMenu)
  }, getToggleButtonProps(), {
    "aria-label": "toggle menu",
    disabled: disabled
  }), /*#__PURE__*/React.createElement(Icon, {
    type: isOpen ? closedIcon : openedIcon,
    outlined: true,
    size: 18,
    color: iconColor
  })))), isOpen && renderChildren({
    inputValue,
    selectedItem,
    highlightedIndex,
    getItemProps,
    getMenuProps,
    closeMenu
  })));
};
export default DropdownWrapper;
//# sourceMappingURL=index.js.map